import { TemplateOptions, CompiledTemplate, ITemplateService } from "../models/template";
import type { IFormatterService } from "../models/formatter";

// Regex patterns for template parsing
const argumentNameRegExp = /^\w+/;
const encodeRegExp = /\$\{([^}]*)\}/g;
const escapedCurlyRegExp = /\\\}/g;
const curlyRegExp = /__CURLY__/g;
const escapedSharpRegExp = /\\#/g;
const sharpRegExp = /__SHARP__/g;

/**
 * Compile a template part (either string literal or code)
 */
function compilePart(part: string, stringPart: boolean): string {
    if (stringPart) {
        return "'" +
            part.split("'").join("\\'")
                .split('\\"').join('\\\\\\"')
                .replace(/\n/g, "\\n")
                .replace(/\r/g, "\\r")
                .replace(/\t/g, "\\t") + "'";
    } else {
        const first = part.charAt(0);
        const rest = part.substring(1);

        if (first === "=") {
            return "+(" + rest + ")+";
        } else if (first === ":") {
            return "+$kendoHtmlEncode(" + rest + ")+";
        } else {
            return ";" + part + ";$kendoOutput+=";
        }
    }
}

/**
 * Template service for compiling and rendering Kendo UI templates
 * 
 * Supports the legacy Kendo template syntax:
 * - #= expression # - Output expression result
 * - #: expression # - Output HTML-encoded expression result
 * - # code # - Execute JavaScript code
 */
export class TemplateService implements ITemplateService {
    paramName = "data";
    useWithBlock = true;
    private debugTemplates = false;

    constructor(private formatterService: IFormatterService) {}

    /**
     * Enable or disable template debugging
     */
    setDebugMode(enabled: boolean): void {
        this.debugTemplates = enabled;
    }

    /**
     * Render a template with an array of data items
     */
    render(template: CompiledTemplate, data: any[]): string {
        let html = "";
        for (let idx = 0; idx < data.length; idx++) {
            html += template(data[idx]);
        }
        return html;
    }

    /**
     * Compile a template string into a function
     */
    compile(template: string | CompiledTemplate, options?: TemplateOptions): CompiledTemplate {
        // If already a function, return as-is
        if (typeof template === "function") {
            return template;
        }

        // Use options values only if they're not undefined (matches original jQuery extend behavior)
        const settings = {
            paramName: options?.paramName !== undefined ? options.paramName : this.paramName,
            useWithBlock: options?.useWithBlock !== undefined ? options.useWithBlock : this.useWithBlock
        };

        const paramName = settings.paramName;
        const argumentName = paramName.match(argumentNameRegExp)![0];
        const useWithBlock = settings.useWithBlock;

        let functionBody = "var $kendoOutput, $kendoHtmlEncode = kendo.htmlEncode;";
        
        functionBody += useWithBlock ? "with(" + paramName + "){" : "";
        functionBody += "$kendoOutput=";

        const parts = template
            .replace(escapedCurlyRegExp, "__CURLY__")
            .replace(encodeRegExp, "#=$kendoHtmlEncode($1)#")
            .replace(curlyRegExp, "}")
            .replace(escapedSharpRegExp, "__SHARP__")
            .split("#");

        for (let idx = 0; idx < parts.length; idx++) {
            functionBody += compilePart(parts[idx], idx % 2 === 0);
        }

        functionBody += useWithBlock ? ";}" : ";";
        functionBody += "return $kendoOutput;";
        functionBody = functionBody.replace(sharpRegExp, "#");

        try {
            // This function evaluation is required for legacy support of the Kendo Template syntax - non CSP compliant.
            // NOSONAR - It is a valid security concern, however this is required for backward compatibility
            const fn = new Function(argumentName, functionBody) as CompiledTemplate;
            fn._slotCount = Math.floor(parts.length / 2);
            return fn;
        } catch (e) {
            if (this.debugTemplates) {
                console.warn(`Invalid template:'${template}' Generated code:'${functionBody}'`);
                // Return a no-op function in debug mode
                return (() => "") as CompiledTemplate;
            } else {
                throw new Error(this.formatterService.format("Invalid template:'{0}' Generated code:'{1}'", template, functionBody));
            }
        }
    }
}
